/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React from 'react';

import {ContextShape, UpdateContextConfig, UserRole} from '../../util/interfaces';

import User from '../../models/userModel';

import Account from '../../models/accountModel';

import {ControlVariant, ThemeProvider, ThemeShape, voidFunction} from '@dvrd/dvr-controls';

import {setBeaconIds} from '../../util/helpscoutUtil';

import {COLOR_BLUE_BLUEMATE} from '../../util/constants';

const deprecated = () => {
    console.warn('Usage of direct onChange functions is deprecated, use onChangeContext directly instead.');
};

const defaultContext: ContextShape = {
    userContext: {
        user: null as User | null,
        validatingUser: true,
        onChange: deprecated
    },
    accountContext: {
        account: null as Account | null,
        accounts: new Array<Account>(),
        onChangeAccounts: deprecated,
        onChange: deprecated
    },
    onChangeContext: voidFunction,
    getCurrentRole: () => null,
    theme: {
        onChange: voidFunction,
        baseColor: '',
        borderColor: '',
        contrastColor: ''
    }
};
export const AppContext = React.createContext(defaultContext);

interface State {
    context: ContextShape;
}

export class AppContextProvider extends React.PureComponent<any, State> {
    constructor(props: any) {
        super(props);
        this.state = {
            context: {
                userContext: {
                    user: null,
                    validatingUser: true,
                    onChange: deprecated
                },
                theme: {
                    baseColor: COLOR_BLUE_BLUEMATE,
                    contrastColor: 'white',
                    controlVariant: ControlVariant.SIMPLE,
                    borderColor: '#D9D9D9',
                    onChange: this.onChangeTheme
                },
                accountContext: {
                    account: null,
                    accounts: new Array<Account>(),
                    onChange: deprecated,
                    onChangeAccounts: deprecated
                },
                onChangeContext: this.onChangeContext,
                getCurrentRole: this.getCurrentRole
            }
        };
    }

    private onChangeTheme = (style?: ThemeShape | null, callback?: () => void) => {
        const context = this.getContext(),
            theme = Object.assign({}, context.theme);
        if (style) {
            if (style.baseColor) theme.baseColor = style.baseColor;
            if (style.contrastColor) theme.contrastColor = style.contrastColor;
        } else {
            theme.baseColor = 'white';
            theme.contrastColor = '#3494ff';
        }
        context.theme = theme;
        this.setState({context}, callback);
    };

    private getContext = (): ContextShape => ({...this.state.context});
    private setContext = (context: ContextShape, callback?: VoidFunction) => {
        this.setState({context}, () => {
            window.setTimeout(() => {
                callback?.();
            });
        });
    };

    private onChangeContext = (config: UpdateContextConfig, callback?: (context: ContextShape) => void) => {
        const {user, account, accounts, validatingUser} = config;
        let context = this.getContext();
        if (user !== undefined) context = this.onChangeUser(user, config, context);
        if (validatingUser !== undefined) context.userContext.validatingUser = validatingUser;
        if (account !== undefined) context = this.onChangeAccount(account, config, context);
        if (accounts !== undefined) context = this.onChangeAccounts(accounts, config, context);
        this.setContext(context, () => {
            callback?.(context);
        });
    };

    private onChangeUser = (user: User | null, config: UpdateContextConfig, context: ContextShape): ContextShape => {
        if (user && context.userContext.user?.id !== user.id) {
            if (config.accounts === undefined) context.accountContext.accounts = user.accounts;
            if (config.account === undefined)
                context.accountContext.account = user.accounts.length ? user.accounts[0] : null;
        }
        context.userContext.user = user;
        return context;
    };

    private onChangeAccount = (
        account: Account | null,
        config: UpdateContextConfig,
        context: ContextShape
    ): ContextShape => {
        context.accountContext.account = account;
        const _user = config.user ?? context.userContext.user;
        if (account) {
            context.accountContext.accounts = this.replaceAccount(account);
            if (_user && account.subscription) {
                _user.subscription = account.subscription;
                context.userContext.user = _user;
            }
        }
        if (_user) setBeaconIds(_user, account);
        return context;
    };

    private onChangeAccounts = (
        accounts: Array<Account>,
        config: UpdateContextConfig,
        context: ContextShape
    ): ContextShape => {
        context.accountContext.accounts = accounts;
        if (config.account === undefined && !context.accountContext.account && accounts.length)
            context.accountContext.account = accounts[0];
        return context;
    };

    private getCurrentRole = (): UserRole | null => {
        const context = this.getContext();
        const {account} = context.accountContext;
        return account?.userRole ?? null;
    };

    private replaceAccount = (account: Account) => {
        const {accounts} = this.getContext().accountContext;
        for (let i = 0; i < accounts.length; i++) {
            if (accounts[i].id === account.id) {
                accounts.splice(i, 1, account);
                break;
            }
        }
        return accounts;
    };

    render = () => {
        const {context} = this.state;
        return (
            <ThemeProvider theme={context.theme}>
                <AppContext.Provider value={context}>{this.props.children}</AppContext.Provider>
            </ThemeProvider>
        );
    };
}
